import React, { useCallback, useEffect, type FC } from 'react';
import { graphql } from 'react-relay';

import AvatarGroup, { type AvatarGroupProps } from '@atlaskit/avatar-group';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { ConfluencePageAri } from '@atlassian/ari';

import { useSubscriptionEvent } from '@confluence/graphql/entry-points/useSubscriptionEvent';
import {
	type ShowFlagArgs,
	type FlagsStateContainer,
} from '@confluence/flags/entry-points/FlagsStateContainer';
import { useQueryInterop, useMutationInterop } from '@confluence/apollo-relay-interop';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';
import { useSpaceRoleMode } from '@confluence/space-utils/entry-points/useSpaceRoleMode';
import { useDialogs } from '@confluence/dialogs/entry-points/useDialogs';
import type { DialogsContainerProps } from '@confluence/dialogs/entry-points/DialogsStateContainer';

import {
	ResolveRestrictionsDialog,
	type ResolveRestrictionsDialogProps,
} from '../resolveRestrictions/ResolveRestrictionsDialog';
import { validateRestrictedPrincipals } from '../resolveRestrictions/validateRestrictedPrincipals';
import { ResourceType } from '../resolveRestrictions/queries/__types__/BlockedAccessRestrictionsQuery';
import type { RestrictedUserPrincipal } from '../commonTypes';
import { preloadSpaceRoleMode } from '../../controller/preloadSpaceRoleMode';

import type {
	useRealtimeAccessRequestsFlagsQuery$data,
	useRealtimeAccessRequestsFlagsQuery$variables,
} from './__generated__/useRealtimeAccessRequestsFlagsQuery.graphql';
import type {
	useRealtimeAccessRequestsFlagsMutation$data,
	useRealtimeAccessRequestsFlagsMutation$variables,
} from './__generated__/useRealtimeAccessRequestsFlagsMutation.graphql';
import type {
	useRealtimeAccessRequestsFlagsResolveRestrictionsForSubjectsMutation$data,
	useRealtimeAccessRequestsFlagsResolveRestrictionsForSubjectsMutation$variables,
} from './__generated__/useRealtimeAccessRequestsFlagsResolveRestrictionsForSubjectsMutation.graphql';
import {
	MINUTE_LOOKBACK,
	isMoreRecentThanCachedRequestTimestamp,
	makeUpdateLastSeenRealtimeRequestCacheCallback,
} from './lastSeenRequestCachingUtils';

const REQUEST_ALREADY_PROCESSED = 'REQUEST_ALREADY_PROCESSED';
const REALTIME_ACCESS_REQUEST_FLAG = 'realtime-access-reqeust-flag';
const REALTIME_ACCESS_REQUEST_FLAG_ID = `${REALTIME_ACCESS_REQUEST_FLAG}-id`;
const SPACE_COLLABORATOR_ROLE_ID = '0c103ff5-44d0-4b2a-b80d-8690442c95f0';
const SPACE_VIEWER_ROLE_ID = '26d75a6e-ad72-42c3-bf30-007d3ebae9ed';

type AccessRequestsList = NonNullable<
	NonNullable<
		useRealtimeAccessRequestsFlagsQuery$data['confluence_getLatestPendingRequests']
	>['latestPendingRequest']
>;

interface RealtimeAccessRequestsFlagsProps {
	contentType: string;
	contentId: string;
	pageTitle: string | null | undefined;
	onReviewRequestsClick: () => void;
	flags: FlagsStateContainer;
}

const NOOP = () => {};

type ResolveRestrictionsDialogWrapperProps = DialogsContainerProps & ResolveRestrictionsDialogProps;

const ResolveRestrictionsDialogWrapper: FC<ResolveRestrictionsDialogWrapperProps> = (props) => (
	<ResolveRestrictionsDialog {...props} />
);

interface FireAnalyticEventArgs {
	eventType: 'sendTrackEvent' | 'sendScreenEvent' | 'sendUIEvent' | 'sendOperationalEvent';
	action: string;
	actionSubject: string;
	actionSubjectId?: string;
	attributes?: any;
	source?: string;
}

const UseRealtimeAccessRequestsQuery = graphql`
	query useRealtimeAccessRequestsFlagsQuery(
		$cloudId: ID!
		$contentId: ID!
		$duration: Int = 5
		$limit: Int = 10
		$start: String
	) {
		confluence_getLatestPendingRequests(
			cloudId: $cloudId
			contentId: $contentId
			duration: $duration
			limit: $limit
			start: $start
		) {
			latestPendingRequest {
				id
				requestAccessType
				creatorAaid
				creationDateTimestamp
				user {
					id
					displayName
					photos {
						isPrimary
						value
					}
				}
			}
		}
	}
`;

export const UseRealtimeAccessRequestsMutation = graphql`
	mutation useRealtimeAccessRequestsFlagsMutation(
		$cloudId: ID!
		$updateContentAccessRequestInput: ConfluenceUpdateContentAccessRequestInput!
	) {
		confluence_updateContentAccessRequest(
			cloudId: $cloudId
			updateContentAccessRequestInput: $updateContentAccessRequestInput
		) {
			success
			errors {
				extensions {
					errorType
					statusCode
					... on ConfluenceUpdateContentAccessRequestMutationErrorExtension {
						displayName
						processedRequestStatus
					}
				}
				message
			}
		}
	}
`;

const ResolveRestrictionsMutation = graphql`
	mutation useRealtimeAccessRequestsFlagsResolveRestrictionsForSubjectsMutation(
		$accessType: ResourceAccessType!
		$contentId: Long!
		$subjects: [BlockedAccessSubjectInput]!
		$spaceRoleId: ID
	) {
		resolveRestrictionsForSubjects(
			input: {
				accessType: $accessType
				contentId: $contentId
				subjects: $subjects
				spaceRoleId: $spaceRoleId
			}
		) {
			success
			errors {
				message
			}
		}
	}
`;

const FlagRemovalDetector: FC<{ onDestroy: () => void; children: React.ReactNode }> = ({
	onDestroy,
	children,
}) => {
	useEffect(() => onDestroy, [onDestroy]);
	return children;
};

/**
 * Assumes requests are already filtered to only those
 * That have displayName and requestAccessType
 * */
export const makeFlagTitle = (requests: AccessRequestsList): string => {
	if (requests.length === 1) {
		const onlyRequest = requests[0];
		const name = onlyRequest?.user?.displayName!;
		const accessType = onlyRequest?.requestAccessType!;

		return `${name} wants to ${accessType?.toLowerCase()} this content`;
	}

	if (requests.length > 1) {
		const firstName = requests[0]?.user?.displayName!;

		if (requests.length === 2) {
			const secondName = requests[1]?.user?.displayName!;
			return `${firstName} and ${secondName} want to access your content`;
		}

		const remainingCount = requests.length - 1;
		return `${firstName} and ${remainingCount} others want to access your content`;
	}

	// Should not happen, because method gets invoked if there's at least one request
	return '';
};

export const makeFlagDescription = (
	requests: AccessRequestsList,
	isRbacEnabled: boolean,
	restrictedUserPrincipalInfo?: RestrictedUserPrincipal,
): string | null => {
	if (requests.length === 1 && restrictedUserPrincipalInfo) {
		const accessType = requests[0]?.requestAccessType!;

		const hasSpaceRestriction = restrictedUserPrincipalInfo.restrictedResources?.some(
			(resource) => resource?.resourceType === ResourceType.SPACE,
		);

		const hasParentRestriction = restrictedUserPrincipalInfo.restrictedResources?.some(
			(resource) => resource?.resourceType !== ResourceType.SPACE,
		);

		const parentRestrictionCount =
			restrictedUserPrincipalInfo.restrictedResources?.reduce((res, curr) => {
				const isParentRestriction = curr?.resourceType !== ResourceType.SPACE;
				return res + (isParentRestriction ? 1 : 0);
			}, 0) ?? 0;

		const spaceRole = isRbacEnabled
			? ` as a ${accessType === 'EDIT' ? 'collaborator' : 'viewer'}`
			: '';

		const items = parentRestrictionCount === 1 ? 'item' : 'items';

		if (hasSpaceRestriction && hasParentRestriction) {
			return `They'll need to be added to the space${spaceRole} and to ${parentRestrictionCount} parent ${items}.`;
		} else if (hasSpaceRestriction) {
			return `They'll need to be added to the space ${spaceRole}.`;
		} else if (hasParentRestriction) {
			return `They'll need to be added to ${parentRestrictionCount} parent ${items}.`;
		}
	}

	return null;
};

export const makeFlagCustomIcon = (requests: AccessRequestsList) => {
	const avatarData: AvatarGroupProps['data'] = requests
		.filter((request) => request?.user?.displayName && request?.requestAccessType)
		.map((request) => ({
			name: request?.user?.displayName!,
			src: request?.user?.photos?.find((photo) => photo?.isPrimary)?.value ?? undefined,
			key: request?.user?.id!,
		}));

	const maxCount = avatarData.length === 1 ? 1 : 2;
	return <AvatarGroup data={avatarData} maxCount={maxCount} />;
};

export const makeFlagActions = (
	requests: AccessRequestsList,
	resolveRestrictions: boolean,
	processRequest: (
		request: AccessRequestsList[number],
		decision: 'APPROVE' | 'DENY',
		resolveRestrictions: boolean,
	) => void,
	onReview: () => void,
	onViewDetails: () => void,
): ShowFlagArgs['actions'] => {
	if (requests.length === 1) {
		const approveRequest = () => processRequest(requests[0], 'APPROVE', resolveRestrictions);
		const primaryAction = {
			content: resolveRestrictions ? 'Add to all' : 'Approve',
			onClick: approveRequest,
		};

		const denyRequest = () => processRequest(requests[0], 'DENY', false);
		const secondaryAction = resolveRestrictions
			? {
					content: 'View details',
					onClick: onViewDetails,
				}
			: {
					content: 'Deny',
					onClick: denyRequest,
				};

		return [primaryAction, secondaryAction];
	}

	if (requests.length > 1) {
		return [{ content: 'Review requests', onClick: onReview }];
	}

	return [];
};

// Called in response to a ping that there is a real-time access request
export const useRealtimeAccessRequestsFlags = ({
	contentId,
	contentType,
	pageTitle,
	flags,
	onReviewRequestsClick: onReviewRequestsClickCallback,
}: RealtimeAccessRequestsFlagsProps) => {
	const { cloudId, edition } = useSessionData();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { isRbacEnabled } = useSpaceRoleMode();
	const { showModal } = useDialogs();

	const fireAnalyticEvent = useCallback(
		(args: FireAnalyticEventArgs) => {
			createAnalyticsEvent({
				type: args.eventType,
				data: {
					source: args.source,
					action: args.action,
					actionSubject: args.actionSubject,
					actionSubjectId: args.actionSubjectId,
					attributes: args.attributes,
				},
			}).fire();
		},
		[createAnalyticsEvent],
	);

	const [mutateRequest] = useMutationInterop<
		useRealtimeAccessRequestsFlagsMutation$data,
		useRealtimeAccessRequestsFlagsMutation$variables
	>(UseRealtimeAccessRequestsMutation);

	const [resolveRestrictionsForSubjects] = useMutationInterop<
		useRealtimeAccessRequestsFlagsResolveRestrictionsForSubjectsMutation$data,
		useRealtimeAccessRequestsFlagsResolveRestrictionsForSubjectsMutation$variables
	>(ResolveRestrictionsMutation);

	const onProcessRequestClick = useCallback(
		async (
			request: AccessRequestsList[number],
			decision: 'APPROVE' | 'DENY',
			resolveRestrictions: boolean,
		) => {
			// Optimistically hide flag to make things look speedy
			void flags.hideFlag(REALTIME_ACCESS_REQUEST_FLAG_ID);

			const result = await mutateRequest({
				variables: {
					cloudId,
					updateContentAccessRequestInput: {
						contentId,
						requestId: request?.id!,
						accessType: request?.requestAccessType!,
						accessRequestedAccountId: request?.user?.id!,
						approvalDecision: decision,
					},
				},
			});

			let successfulMutation = result.data?.confluence_updateContentAccessRequest?.success;
			const requestAlreadyProcessed =
				result?.data?.confluence_updateContentAccessRequest?.errors?.find(
					(err) => err?.message === REQUEST_ALREADY_PROCESSED,
				);

			// If user is blocked, run mutation to resolve restrictions
			if (successfulMutation && decision === 'APPROVE' && resolveRestrictions) {
				const rbacSpaceRole =
					request?.requestAccessType === 'EDIT' ? SPACE_COLLABORATOR_ROLE_ID : SPACE_VIEWER_ROLE_ID;
				const spaceRoleId = isRbacEnabled ? rbacSpaceRole : null;
				const subjectId = request?.creatorAaid!;
				const subjectType = 'USER';

				const result = await resolveRestrictionsForSubjects({
					variables: {
						contentId: Number(contentId),
						accessType: request?.requestAccessType!,
						subjects: [{ subjectId, subjectType }],
						spaceRoleId,
					},
				});

				successfulMutation = result?.data?.resolveRestrictionsForSubjects?.success;
			}

			if (successfulMutation) {
				void flags.showSuccessFlag({
					id: `${REALTIME_ACCESS_REQUEST_FLAG_ID}_SUCCESS`,
					title: `Request ${decision === 'APPROVE' ? 'approved' : 'denied'}`,
					description:
						decision === 'APPROVE'
							? `${request?.user?.displayName} can now successfully ${request?.requestAccessType?.toLowerCase()} this content`
							: `${request?.user?.displayName}'s access to this content won't change`,
					isAutoDismiss: true,
				});
			} else if (requestAlreadyProcessed) {
				void flags.showInfoFlag({
					id: `${REALTIME_ACCESS_REQUEST_FLAG_ID}_INFO`,
					title: 'Someone already responded to this this request',
					isAutoDismiss: true,
				});
			} else {
				void flags.showErrorFlag({
					id: `${REALTIME_ACCESS_REQUEST_FLAG_ID}_ERROR`,
					title: 'We ran into a problem resolving the access request. Try again later.',
					isAutoDismiss: true,
				});
			}
		},
		[cloudId, contentId, isRbacEnabled, flags, mutateRequest, resolveRestrictionsForSubjects],
	);

	const onReviewRequestsClick = useCallback(() => {
		onReviewRequestsClickCallback();
		void flags.hideFlag(REALTIME_ACCESS_REQUEST_FLAG_ID);
	}, [onReviewRequestsClickCallback, flags]);

	const onViewDetails = useCallback(
		(props: ResolveRestrictionsDialogProps) => {
			showModal(ResolveRestrictionsDialogWrapper, props);
			void flags.hideFlag(REALTIME_ACCESS_REQUEST_FLAG_ID);
		},
		[showModal, flags],
	);

	const maybeShowRealtimeFlag = useCallback(
		async (data: useRealtimeAccessRequestsFlagsQuery$data) => {
			// These requests are already less than 5 minues old straight from the API
			const accessRequests = data.confluence_getLatestPendingRequests?.latestPendingRequest ?? [];

			// We just need to remove requests older than the "last seen" one in the cache
			// Also ensure all requests have bare minimum info to display flag correctly
			const filteredRequests = accessRequests.filter((request) => {
				const hasAllNecessaryData = request?.user?.displayName && request?.requestAccessType;
				const requestCreationTimestamp = Number(request?.creationDateTimestamp);
				const isMoreRecentThanLastSeenRequest = isMoreRecentThanCachedRequestTimestamp(
					contentId,
					requestCreationTimestamp,
				);

				return hasAllNecessaryData && isMoreRecentThanLastSeenRequest;
			});

			const latestIncomingRequestCreatedAt = Number(filteredRequests[0]?.creationDateTimestamp);
			const cacheLatestRequest = makeUpdateLastSeenRealtimeRequestCacheCallback(
				contentId,
				latestIncomingRequestCreatedAt,
			);

			if (filteredRequests.length === 0) {
				return;
			}

			let restrictedUserPrincipalInfo: RestrictedUserPrincipal | undefined;
			let resolveRestrictionsDialogProps: ResolveRestrictionsDialogProps | undefined;
			let shouldHandleBlockedAccess: boolean = false;
			let onViewDetailsClick: () => void = NOOP;

			if (filteredRequests.length === 1 && edition) {
				const request = filteredRequests[0];
				const user = request?.user;
				const accessType = request?.requestAccessType ?? 'VIEW';
				const addMode = accessType === 'EDIT' ? 'as-editor' : 'as-viewer';
				const userPrincipal = {
					id: user?.id ?? '',
					name: user?.displayName ?? '',
					avatarUrl: user?.photos?.find((photo) => photo?.isPrimary)?.value ?? '',
					email: '', // not needed
					knownErrors: undefined,
					sitePermissionType: accessType,
				};

				// Make the blocked access check
				const [
					{
						showResolveRestrictionsDialog,
						showWarnRestrictionsDialog,
						restrictedUserPrincipals,
						assignableSpaceRolesIntersection,
					},
				] = await Promise.all([
					validateRestrictedPrincipals({
						contentId,
						cloudId,
						edition,
						numberOfAncestors: 1, // not needed, API will error out if < 10 layers of ancestors
						addMode,
						stagedUserPrincipals: [userPrincipal],
						setMasterState: () => {}, // not needed
						fireAnalyticEvent,
					}),
					preloadSpaceRoleMode(cloudId),
				]);

				if (showResolveRestrictionsDialog && restrictedUserPrincipals?.[0]) {
					restrictedUserPrincipalInfo = restrictedUserPrincipals?.[0];
					shouldHandleBlockedAccess = !!restrictedUserPrincipalInfo;

					onViewDetailsClick = () => onViewDetails(resolveRestrictionsDialogProps!);

					resolveRestrictionsDialogProps = {
						contentId,
						restrictedUserPrincipals,
						assignableSpaceRolesIntersection,
						pageTitle,
						addMode,
						onComplete: () => onProcessRequestClick(request, 'APPROVE', true),
						onClose: NOOP,
						onCloseComplete: NOOP,
						onSkip: NOOP,
						showErrorFlag: flags.showErrorFlag,
						triggeredBy: 'realtimeAccessRequestFlag',
					};
				}

				if (showWarnRestrictionsDialog) {
					// ToDo: Send some analytic for tracking purposes
					// And do nothing else. We only show the flag for users who
					// Are empowered to remove the blockages
					return;
				}
			}

			// Only hiding any currently-displayed flag if there's another flag to show (filteredRequests > 0)
			// Need the await to ensure proper closing animation of currently-displayed realtime flag
			await flags.hideFlag(REALTIME_ACCESS_REQUEST_FLAG_ID);

			await flags.showCustomFlag({
				id: REALTIME_ACCESS_REQUEST_FLAG_ID,
				isAutoDismiss: true,
				autoDismissSeconds: 16,
				customIcon: makeFlagCustomIcon(filteredRequests),
				title: (
					<FlagRemovalDetector onDestroy={cacheLatestRequest}>
						{makeFlagTitle(filteredRequests)}
					</FlagRemovalDetector>
				),
				description: makeFlagDescription(
					filteredRequests,
					isRbacEnabled,
					restrictedUserPrincipalInfo,
				),
				actions: makeFlagActions(
					filteredRequests,
					shouldHandleBlockedAccess,
					onProcessRequestClick,
					onReviewRequestsClick,
					onViewDetailsClick,
				),
			});
		},
		[
			cloudId,
			contentId,
			pageTitle,
			edition,
			isRbacEnabled,
			onProcessRequestClick,
			onReviewRequestsClick,
			onViewDetails,
			fireAnalyticEvent,
			flags,
		],
	);

	const { refetch } = useQueryInterop<
		useRealtimeAccessRequestsFlagsQuery$data,
		useRealtimeAccessRequestsFlagsQuery$variables
	>(UseRealtimeAccessRequestsQuery, {
		notifyOnNetworkStatusChange: true,
		fetchPolicy: 'network-only',
		variables: {
			cloudId,
			contentId,
			duration: MINUTE_LOOKBACK,
		},
		onCompleted: maybeShowRealtimeFlag,
	});

	// Need this bc useSubscriptionEvent passes unwanted arguments to refetch
	const refetchAccessRequests = useCallback(() => refetch(), [refetch]);

	useSubscriptionEvent({
		contentId,
		contentType,
		delta: 'contentAccessRequested',
		subscriberId: ConfluencePageAri.create({
			siteId: cloudId,
			pageId: contentId,
		}).toString(),
		onDataReceived: refetchAccessRequests,
		/** TODO: Also monitor for tab inactivity and disable the subscription */
		skip: false,
	});
};
